import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import './Modal.css';

interface CreateDealerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: any) => void;
}

interface DealerFormData {
  dealer_name: string;
  dealer_id: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  district_manager: string;
  regional_manager: string;
  email?: string;
  phone?: string;
}

interface DistrictManager {
  manager_name: string;
  regional_manager: string;
}

const CreateDealerModal: React.FC<CreateDealerModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<DealerFormData>({
    dealer_name: '',
    dealer_id: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    district_manager: '',
    regional_manager: '',
    email: '',
    phone: '',
  });

  const [districtManagers, setDistrictManagers] = useState<DistrictManager[]>([]);
  const [regionalManagers, setRegionalManagers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [geocodePreview, setGeocodePreview] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      fetchManagers();
    }
  }, [isOpen]);

  const fetchManagers = async () => {
    setLoading(true);
    try {
      // Fetch district and regional managers
      const [districtResponse, regionalResponse] = await Promise.all([
        adminAPI.listManagers({ role: 'district', per_page: 1000 }),
        adminAPI.listManagers({ role: 'regional', per_page: 1000 }),
      ]);

      if (districtResponse.success) {
        const districts = districtResponse.managers.map((m: any) => ({
          manager_name: m.manager_name,
          regional_manager: m.reports_to || '',
        }));
        setDistrictManagers(districts);
      }

      if (regionalResponse.success) {
        const regionals = regionalResponse.managers.map((m: any) => m.manager_name);
        setRegionalManagers(regionals);
      }
    } catch (err) {
      console.error('Error fetching managers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });

    // Auto-populate regional manager when district manager is selected
    if (name === 'district_manager') {
      const district = districtManagers.find((d) => d.manager_name === value);
      if (district && district.regional_manager) {
        setFormData((prev) => ({ ...prev, regional_manager: district.regional_manager }));
      }
    }

    // Update geocode preview when address changes
    if (['address', 'city', 'state', 'zip'].includes(name)) {
      updateGeocodePreview({ ...formData, [name]: value });
    }
  };

  const updateGeocodePreview = (data: DealerFormData) => {
    const parts = [data.address, data.city, data.state, data.zip].filter(Boolean);
    setGeocodePreview(parts.join(', '));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.dealer_name.trim()) {
      newErrors.dealer_name = 'Dealer name is required';
    }

    if (!formData.dealer_id.trim()) {
      newErrors.dealer_id = 'Dealer ID is required';
    } else if (!/^\d{8}$/.test(formData.dealer_id)) {
      newErrors.dealer_id = 'Dealer ID must be exactly 8 digits';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    } else if (!/^[A-Z]{2}$/.test(formData.state.toUpperCase())) {
      newErrors.state = 'State must be 2-letter code (e.g., CO)';
    }

    if (!formData.zip.trim()) {
      newErrors.zip = 'ZIP code is required';
    } else if (!/^\d{5}$/.test(formData.zip)) {
      newErrors.zip = 'ZIP code must be exactly 5 digits';
    }

    if (!formData.district_manager) {
      newErrors.district_manager = 'District manager is required';
    }

    if (!formData.regional_manager) {
      newErrors.regional_manager = 'Regional manager is required';
    }

    // Optional email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        state: formData.state.toUpperCase(),
      };

      // Remove empty optional fields
      if (!submitData.email) delete submitData.email;
      if (!submitData.phone) delete submitData.phone;

      const response = await adminAPI.createDealer(submitData);

      if (response.success) {
        onSuccess(response);
        handleClose();
      } else {
        setErrors({ submit: response.error || 'Failed to create dealer' });
      }
    } catch (err: any) {
      console.error('Error creating dealer:', err);
      setErrors({
        submit: err.response?.data?.error || err.message || 'Failed to create dealer',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      dealer_name: '',
      dealer_id: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      district_manager: '',
      regional_manager: '',
      email: '',
      phone: '',
    });
    setErrors({});
    setGeocodePreview('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <span className="material-icons">store</span>
            Add New Dealer
          </h2>
          <button className="modal-close" onClick={handleClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-body">
            {errors.submit && (
              <div className="form-error-banner">
                <span className="material-icons">error</span>
                {errors.submit}
              </div>
            )}

            {/* Dealer Information */}
            <div className="form-section">
              <h3>Dealer Information</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dealer_name">
                    Dealer Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="dealer_name"
                    name="dealer_name"
                    value={formData.dealer_name}
                    onChange={handleInputChange}
                    className={errors.dealer_name ? 'error' : ''}
                    placeholder="e.g., Smith Equipment LLC"
                  />
                  {errors.dealer_name && <span className="field-error">{errors.dealer_name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="dealer_id">
                    Dealer ID <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="dealer_id"
                    name="dealer_id"
                    value={formData.dealer_id}
                    onChange={handleInputChange}
                    className={errors.dealer_id ? 'error' : ''}
                    placeholder="8-digit ID (e.g., 78925900)"
                    maxLength={8}
                  />
                  {errors.dealer_id && <span className="field-error">{errors.dealer_id}</span>}
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className="form-section">
              <h3>Location Information</h3>

              <div className="form-group">
                <label htmlFor="address">
                  Street Address <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={errors.address ? 'error' : ''}
                  placeholder="e.g., 123 Main Street"
                />
                {errors.address && <span className="field-error">{errors.address}</span>}
              </div>

              <div className="form-row form-row-3">
                <div className="form-group">
                  <label htmlFor="city">
                    City <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className={errors.city ? 'error' : ''}
                    placeholder="e.g., Denver"
                  />
                  {errors.city && <span className="field-error">{errors.city}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="state">
                    State <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className={errors.state ? 'error' : ''}
                    placeholder="CO"
                    maxLength={2}
                    style={{ textTransform: 'uppercase' }}
                  />
                  {errors.state && <span className="field-error">{errors.state}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="zip">
                    ZIP Code <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="zip"
                    name="zip"
                    value={formData.zip}
                    onChange={handleInputChange}
                    className={errors.zip ? 'error' : ''}
                    placeholder="80202"
                    maxLength={5}
                  />
                  {errors.zip && <span className="field-error">{errors.zip}</span>}
                </div>
              </div>

              {geocodePreview && (
                <div className="geocode-preview">
                  <span className="material-icons">location_on</span>
                  <div>
                    <strong>Address Preview:</strong>
                    <p>{geocodePreview}</p>
                    <small>This address will be geocoded automatically</small>
                  </div>
                </div>
              )}
            </div>

            {/* Hierarchy Assignment */}
            <div className="form-section">
              <h3>Management Hierarchy</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="district_manager">
                    District Manager <span className="required">*</span>
                  </label>
                  <select
                    id="district_manager"
                    name="district_manager"
                    value={formData.district_manager}
                    onChange={handleInputChange}
                    className={errors.district_manager ? 'error' : ''}
                  >
                    <option value="">Select District Manager</option>
                    {districtManagers.map((dm) => (
                      <option key={dm.manager_name} value={dm.manager_name}>
                        {dm.manager_name}
                      </option>
                    ))}
                  </select>
                  {errors.district_manager && (
                    <span className="field-error">{errors.district_manager}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="regional_manager">
                    Regional Manager <span className="required">*</span>
                  </label>
                  <select
                    id="regional_manager"
                    name="regional_manager"
                    value={formData.regional_manager}
                    onChange={handleInputChange}
                    className={errors.regional_manager ? 'error' : ''}
                  >
                    <option value="">Select Regional Manager</option>
                    {regionalManagers.map((rm) => (
                      <option key={rm} value={rm}>
                        {rm}
                      </option>
                    ))}
                  </select>
                  {errors.regional_manager && (
                    <span className="field-error">{errors.regional_manager}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information (Optional) */}
            <div className="form-section">
              <h3>Contact Information (Optional)</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? 'error' : ''}
                    placeholder="dealer@example.com"
                  />
                  {errors.email && <span className="field-error">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="555-1234"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting || loading}>
              {submitting ? (
                <>
                  <span className="button-spinner"></span>
                  Creating Dealer...
                </>
              ) : (
                <>
                  <span className="material-icons">add</span>
                  Create Dealer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateDealerModal;
